// ProjectFilters.jsx
import { Box, Button, FormControlLabel, Checkbox, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ClearIcon from "@mui/icons-material/Clear";
import moment from "moment";
import CustomAutocomplete from "./CustomMultiselect/customAutocomplete";
import { useMemo } from "react";

const ProjectFilters = ({
  showFilters,
  units,
  activities,
  statuses,
  projectManagers,
  employees,
  clearAllFilters,

  showMyProjects,
  showMyUnitProjects,
  startDateFrom,
  startDateTo,
  endDateFrom,
  endDateTo,
  internal,
  searchFields,
  filterFields,

  setShowMyProjects,
  setShowMyUnitProjects,
  setStartDateFrom,
  setStartDateTo,
  setEndDateFrom,
  setEndDateTo,
  setInternal,
  setSearchFields,
  setFilterFields,
}) => {
  const fieldOptions = useMemo(() => {
    return [
      {
        inputType: "text",
        key: "project_manager_email",
        label: "Project Manager",
        getOptionLabel: (option) => `${option.name} (${option.email})`,
        innerOptions: projectManagers,
        getOptionValue: (option) => option.email,
      },
      {
        inputType: "text",
        key: "$members.email$",
        label: "Project Member",
        getOptionLabel: (option) => `${option.name} (${option.email})`,
        innerOptions: employees,
        getOptionValue: (option) => option.email,
      },
      {
        inputType: "text",
        key: "$Unit.unit_id$",
        label: "Unit",
        innerOptions: units,
        getOptionLabel: (option) => option.unit_name,
        getOptionValue: (option) => ({
          value: option.unit_id,
          label: option.unit_name,
        }),
      },
      {
        inputType: "text",
        key: "$Activity.activity_id$",
        label: "Activity",
        innerOptions: activities,
        getOptionLabel: (option) => option.activity_name,
        getOptionValue: (option) => ({
          value: option.activity_id,
          label: option.activity_name,
        }),
      },
      {
        inputType: "text",
        key: "status_id",
        label: "Status",
        innerOptions: statuses,
        getOptionLabel: (option) => option.status_name,
        getOptionValue: (option) => ({
          value: option.status_id,
          label: option.status_name,
        }),
      },
      { inputType: "text", key: "code", label: "Code (starts with)" },
      { inputType: "text", key: "client_name", label: "Client name" },
      {
        inputType: "text",
        key: "expenses_confirmed_by_name",
        label: "Expenses confirmed by name",
      },
      {
        inputType: "text",
        key: "name",
        label: "Project name",
      },
      {
        inputType: "text",
        key: "description",
        label: "Project description",
      },
      {
        inputType: "text",
        key: "notes",
        label: "Project notes",
      },
    ];
  }, [activities, employees, projectManagers, statuses, units]);

  return showFilters ? (
    <Box className="filtersContainer">
      <Stack direction="column" gap={1}>
        <Stack direction="column" gap={1}>
          <CustomAutocomplete
            options={fieldOptions}
            placeholder="Search"
            selected={searchFields}
            setSelected={setSearchFields}
            helperText="Finds projects where ANY of the conditions are met"
            defaultOption={true}
            defaultKey="all"
            defaultLabel="Search"
            defaultInputType="Text"
            autoFocus
          ></CustomAutocomplete>
          <CustomAutocomplete
            options={fieldOptions}
            placeholder="Filter"
            selected={filterFields}
            setSelected={setFilterFields}
            helperText="Finds projects where ALL of the conditions are met"
          ></CustomAutocomplete>
        </Stack>
        <Stack direction="row" gap={2}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Start Date From"
              value={startDateFrom ? moment(startDateFrom, "YYYY-MM-DD") : null}
              onChange={(newValue) =>
                setStartDateFrom(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
              format="DD.MM.YYYY"
            />

            <DatePicker
              label="Start Date To"
              value={startDateTo ? moment(startDateTo, "YYYY-MM-DD") : null}
              onChange={(newValue) =>
                setStartDateTo(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
              format="DD.MM.YYYY"
            />

            <DatePicker
              label="End Date From"
              value={endDateFrom ? moment(endDateFrom, "YYYY-MM-DD") : null}
              onChange={(newValue) =>
                setEndDateFrom(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
              format="DD.MM.YYYY"
            />

            <DatePicker
              label="End Date To"
              value={endDateTo ? moment(endDateTo, "YYYY-MM-DD") : null}
              onChange={(newValue) =>
                setEndDateTo(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
              format="DD.MM.YYYY"
            />
          </LocalizationProvider>
          <FormControlLabel
            control={
              <Checkbox
                checked={showMyProjects}
                onChange={(e) => setShowMyProjects(e.target.checked)}
              />
            }
            label="Show My Projects"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showMyUnitProjects}
                onChange={(e) => setShowMyUnitProjects(e.target.checked)}
              />
            }
            label="Show My Unit Projects"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={internal}
                onChange={(e) => setInternal(e.target.checked)}
              />
            }
            label="Show Internal Projects"
          />
        </Stack>
      </Stack>
      <Button
        variant="outlined"
        sx={{ mt: 2 }}
        onClick={clearAllFilters}
        startIcon={<ClearIcon />}
      >
        Clear All Filters
      </Button>
    </Box>
  ) : (
    <CustomAutocomplete
      options={fieldOptions}
      placeholder="Search"
      selected={searchFields}
      setSelected={setSearchFields}
      helperText="Finds projects where ANY of the conditions are met"
      defaultOption={true}
      defaultKey="all"
      defaultLabel="Search"
      defaultInputType="Text"
      autoFocus
    ></CustomAutocomplete>
  );
};

export default ProjectFilters;
